import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  AlertTriangle,
  Factory,
  Package,
  Printer,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { productionService } from '@services/productionService';
import { PendingManufacturingProduct, PendingManufacturingOrder } from '@/types/production';

/**
 * Formata data ISO para dd/mm/aaaa sem deslocamento de fuso em datas puras (YYYY-MM-DD)
 */
const formatDate = (value?: string): string => {
  if (!value) return '-';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR');
};

/** Número do pedido: ERP > E-commerce > ID interno */
const getOrderNumber = (order: PendingManufacturingOrder): string =>
  order.order_num_erp || order.order_num_ecommerce || String(order.order_id);

/**
 * Relatório de Produtos a Fabricar — produtos de fabricação própria
 * demandados por pedidos de venda pendentes de produção (status "Em digitação").
 */
export const PendingManufacturingView: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<PendingManufacturingProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await productionService.getPendingManufacturing();
      setItems(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar produtos a fabricar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const toggleExpand = (productId: number) => {
    setExpanded((prev) => ({ ...prev, [productId]: !prev[productId] }));
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      setError(null);
      await productionService.downloadPendingManufacturingPdf();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao gerar PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleGenerateOp = () => {
    // IDs únicos de todos os pedidos aguardando produção
    const salesOrderIds = Array.from(
      new Set(items.flatMap((p) => (p.orders || []).map((o) => o.order_id)))
    );
    const suggestedItems = items.map((p) => ({
      product_id: p.product_id,
      sku: p.sku,
      description: p.description,
      quantity: p.total_quantity,
    }));
    navigate('/production/orders/new', { state: { salesOrderIds, suggestedItems } });
  };

  const totalUnits = items.reduce((sum, p) => sum + (p.total_quantity || 0), 0);
  const totalOrders = items.reduce((sum, p) => sum + (p.orders_count || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Produtos a Fabricar</h1>
          <p className="text-slate-600">
            Produtos de fabricação própria demandados por pedidos pendentes de produção
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadPdf}
            icon={<Printer size={18} />}
            loading={downloadingPdf}
            disabled={loading}
          >
            {downloadingPdf ? 'Gerando PDF...' : 'Imprimir PDF'}
          </Button>
          <PermissionGuard permission="production.create">
            <Button
              variant="secondary"
              onClick={handleGenerateOp}
              icon={<Factory size={18} />}
              disabled={loading || items.length === 0}
            >
              Gerar OP
            </Button>
          </PermissionGuard>
          <Button onClick={loadData} icon={<RefreshCw size={18} />} disabled={loading}>
            {loading ? 'Atualizando...' : 'Atualizar'}
          </Button>
        </div>
      </div>

      {/* Estatísticas rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Factory className="text-emerald-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {items.length.toLocaleString('pt-BR')}
                </p>
                <p className="text-small text-slate-500">Produtos a fabricar</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {totalUnits.toLocaleString('pt-BR')}
                </p>
                <p className="text-small text-slate-500">Unidades demandadas</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <ClipboardList className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {totalOrders.toLocaleString('pt-BR')}
                </p>
                <p className="text-small text-slate-500">Pedidos aguardando</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={18} />
              <p className="text-red-600">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de produtos */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-500">Carregando produtos a fabricar...</p>
            </div>
          ) : items.length === 0 && !error ? (
            <div className="text-center py-8">
              <ClipboardList className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500">
                Nenhum produto pendente de fabricação no momento
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <th className="w-10"></th>
                  <th className="text-left">SKU</th>
                  <th className="text-left">Descrição</th>
                  <th className="text-right">Qtde Total</th>
                  <th className="text-center">Nº de Pedidos</th>
                  <th className="text-center">Pedido Mais Antigo</th>
                </tr>
              </TableHeader>
              <tbody>
                {items.map((product) => (
                  <React.Fragment key={product.product_id}>
                    <TableRow className="cursor-pointer hover:bg-slate-50">
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => toggleExpand(product.product_id)}
                          className="p-1 text-slate-500 hover:text-slate-700 rounded"
                          title={expanded[product.product_id] ? 'Recolher pedidos' : 'Ver pedidos'}
                        >
                          {expanded[product.product_id] ? (
                            <ChevronDown size={18} />
                          ) : (
                            <ChevronRight size={18} />
                          )}
                        </button>
                      </TableCell>
                      <TableCell>
                        <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                          {product.sku}
                        </code>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-slate-900">
                          {product.description || '-'}
                        </p>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-bold text-slate-900">
                          {product.total_quantity.toLocaleString('pt-BR')}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          {product.orders_count} pedido{product.orders_count !== 1 ? 's' : ''}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm text-slate-700">
                          {formatDate(product.oldest_order_date)}
                        </span>
                      </TableCell>
                    </TableRow>

                    {expanded[product.product_id] && (
                      <tr>
                        <td colSpan={6} className="bg-slate-50 px-6 py-4">
                          <p className="text-sm font-medium text-slate-700 mb-2">
                            Pedidos aguardando produção
                          </p>
                          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-slate-100 text-slate-600">
                                  <th className="text-left px-4 py-2 font-medium">Pedido</th>
                                  <th className="text-center px-4 py-2 font-medium">Data</th>
                                  <th className="text-left px-4 py-2 font-medium">Cliente</th>
                                  <th className="text-right px-4 py-2 font-medium">Quantidade</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(product.orders || []).map((order) => (
                                  <tr
                                    key={order.order_id}
                                    className="border-t border-slate-100"
                                  >
                                    <td className="px-4 py-2">
                                      <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                                        {getOrderNumber(order)}
                                      </code>
                                    </td>
                                    <td className="px-4 py-2 text-center text-slate-700">
                                      {formatDate(order.order_date)}
                                    </td>
                                    <td className="px-4 py-2 text-slate-700">
                                      {order.customer_name || '-'}
                                    </td>
                                    <td className="px-4 py-2 text-right font-medium text-slate-900">
                                      {order.quantity.toLocaleString('pt-BR')}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
